import { useRef, useState } from "react";
import { motion, useSpring } from "framer-motion";
import { Mail, Package, Plus } from "lucide-react";
import { eur, eur2 } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, index }) {
  const { addItem, setInquiryProduct } = useCart();
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const [colorIdx, setColorIdx] = useState(0);
  const runs = product.size_options ? product.size_options.map((o) => o.run) : product.size_runs;
  const [sizeRun, setSizeRun] = useState(runs[0]);

  const boxSize = product.size_options
    ? (product.size_options.find((o) => o.run === sizeRun)?.box_size ?? product.size_options[0].box_size)
    : product.box_size;

  const color = product.colors[colorIdx];
  const rx = useSpring(0, { stiffness: 220, damping: 22 });
  const ry = useSpring(0, { stiffness: 220, damping: 22 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 14);
    rx.set(-py * 11);
  };

  const onLeave = () => {
    rx.set(0);
    ry.set(0);
    setHover(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 56, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.25 } }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (index % 6) * 0.07 }}
      className="group"
      style={{ perspective: 1000 }}
      data-testid={`product-card-${product.id}`}
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={onLeave}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className={`border border-black/10 bg-white transition-shadow duration-500 ${
          hover ? "shadow-[0_40px_80px_-24px_rgba(11,12,14,0.35)]" : "shadow-none"
        }`}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-skytint/60 to-paper">
          <img
            key={`p-${color.name}`}
            src={color.images.profile}
            alt={`${product.name} ${color.name}`}
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
              hover ? "opacity-0" : "opacity-100"
            }`}
            data-testid={`product-image-profile-${product.id}`}
          />
          <img
            key={`a-${color.name}`}
            src={color.images.angled}
            alt={`${product.name} ${color.name} — vista alternativa`}
            loading="lazy"
            className={`absolute inset-0 h-full w-full scale-105 object-cover transition-opacity duration-700 ${
              hover ? "opacity-100" : "opacity-0"
            }`}
            data-testid={`product-image-angled-${product.id}`}
          />
          <div className="absolute left-4 top-4 flex flex-col gap-2" style={{ transform: "translateZ(40px)" }}>
            <span className="flex items-center gap-1.5 bg-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white" data-testid={`product-box-${product.id}`}>
              <Package className="h-3 w-3" />
              Box {boxSize} paia
            </span>
            <span className="bg-white/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink backdrop-blur-sm">
              {product.sku} · 1 colore/box
            </span>
          </div>
          <span
            className="absolute right-4 top-4 bg-cobalt px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white"
            style={{ transform: "translateZ(40px)" }}
            data-testid={`product-color-label-${product.id}`}
          >
            {color.name}
          </span>
        </div>

        <div className="p-6" style={{ transform: "translateZ(24px)" }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracotta">{product.category}</p>
          <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-ink" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-ink/55">{product.blurb}</p>

          {/* Color selector — ogni box è per singolo colore */}
          <div className="mt-4">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">Colore del box</p>
            <div className="flex flex-wrap gap-1.5" data-testid={`product-colors-${product.id}`}>
              {product.colors.map((c, i) => (
                <button
                  key={c.name}
                  onClick={() => setColorIdx(i)}
                  className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                    i === colorIdx
                      ? "border-ink bg-ink text-white"
                      : "border-black/15 text-ink/55 hover:border-ink hover:text-ink"
                  }`}
                  data-testid={`product-color-${product.id}-${c.name.toLowerCase().replace(/ /g, "-")}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Assortimento taglie */}
          {runs.length > 1 && (
            <div className="mt-3">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">Assortimento taglie</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`product-sizes-${product.id}`}>
                {runs.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSizeRun(s)}
                    className={`border px-3 py-1.5 font-mono text-[10px] tracking-[0.08em] transition-colors duration-300 ${
                      s === sizeRun
                        ? "border-ink bg-ink text-white"
                        : "border-black/15 text-ink/55 hover:border-ink hover:text-ink"
                    }`}
                    data-testid={`product-size-${product.id}-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-end justify-between border-t border-black/10 pt-4">
            <div>
              <p className="font-mono text-lg font-semibold text-ink" data-testid={`product-price-${product.id}`}>
                {eur2(product.pair_price)}
                <span className="ml-1 text-[10px] font-normal text-ink/45">/paio</span>
              </p>
              <p className="font-mono text-[10px] text-ink/45">
                box da {boxSize} = {eur(product.pair_price * boxSize)}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setInquiryProduct(product)}
                aria-label={`Richiedi informazioni su ${product.name}`}
                className="flex h-10 w-10 items-center justify-center border border-ink/15 text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-white"
                data-testid={`product-inquiry-${product.id}`}
              >
                <Mail className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => addItem(product, color.name, sizeRun, boxSize, { x: e.clientX, y: e.clientY })}
                className="flex h-10 items-center gap-2 bg-ink px-4 font-mono text-[10px] uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-cobalt"
                data-testid={`product-add-to-order-${product.id}`}
              >
                <Plus className="h-3.5 w-3.5" />
                1 Box
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
